import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallState {
  deferredPrompt: BeforeInstallPromptEvent | null;
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isMobile: boolean;
  isStandalone: boolean;
}

const DISMISS_KEY = 'pwa_install_dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

export function usePWAInstall() {
  const [state, setState] = useState<PWAInstallState>({
    deferredPrompt: null,
    isInstallable: false,
    isInstalled: false,
    isIOS: false,
    isMobile: false,
    isStandalone: false,
  });

  // Check if running as standalone (installed PWA)
  const checkIsStandalone = useCallback(() => {
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://');
    return isStandalone;
  }, []);

  // Check device type
  const checkDeviceType = useCallback(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    return { isIOS, isMobile };
  }, []);

  // Check if dismissed within the last 7 days
  const isDismissed = useCallback(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    
    const dismissedTime = parseInt(dismissedAt, 10);
    const now = Date.now();
    return now - dismissedTime < DISMISS_DURATION;
  }, []);

  // Initialize state
  useEffect(() => {
    const { isIOS, isMobile } = checkDeviceType();
    const isStandalone = checkIsStandalone();
    
    setState(prev => ({
      ...prev,
      isIOS,
      isMobile,
      isStandalone,
      isInstalled: isStandalone,
      // iOS is always "installable" via manual method
      isInstallable: isIOS && !isStandalone,
    }));
  }, [checkDeviceType, checkIsStandalone]);

  // Listen for beforeinstallprompt event (Android/Chrome)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      
      setState(prev => ({
        ...prev,
        deferredPrompt: promptEvent,
        isInstallable: true,
      }));
    };

    const handleAppInstalled = () => {
      setState(prev => ({
        ...prev,
        deferredPrompt: null,
        isInstallable: false,
        isInstalled: true,
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Prompt install for Android/Chrome
  const promptInstall = useCallback(async () => {
    if (!state.deferredPrompt) {
      return { success: false, reason: 'no_prompt' };
    }

    try {
      await state.deferredPrompt.prompt();
      const { outcome } = await state.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setState(prev => ({
          ...prev,
          deferredPrompt: null,
          isInstallable: false,
        }));
        return { success: true, outcome };
      }
      
      return { success: false, outcome };
    } catch (error) {
      console.error('Error prompting install:', error);
      return { success: false, reason: 'error' };
    }
  }, [state.deferredPrompt]);

  // Dismiss the install prompt for 7 days
  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  }, []);

  // Check if should show banner
  const shouldShowBanner = useCallback((forceShow = false) => {
    // Force show for testing via query param
    if (forceShow) return true;
    
    if (state.isInstalled || state.isStandalone) return false;
    if (isDismissed()) return false;
    
    // Show for mobile (iOS or Android with prompt)
    if (state.isMobile) {
      return state.isIOS || state.isInstallable;
    }
    
    // Also show on Desktop Chrome/Edge if installable (has deferred prompt)
    return state.isInstallable;
  }, [state.isMobile, state.isInstalled, state.isStandalone, state.isIOS, state.isInstallable, isDismissed]);

  return {
    ...state,
    promptInstall,
    dismissPrompt,
    shouldShowBanner,
    isDismissed,
    canPromptDirectly: !!state.deferredPrompt,
  };
}
